import { Component, computed, DestroyRef, ElementRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { AlertaService } from '../../../../../shared/utils/alerta.service';
import { CategoriaDoFavorito, ServicoFavorito } from '../../../models/servicos-favoritos.model';
import { CatalogoPublicoService } from '../../../services/catalogo-publico.service';
import { FavoritosService, montarFavoritos } from '../../../services/favoritos.service';

type Ordenacao = 'acessos' | 'nome';

/** Um estado por vez: o que a área da lista mostra. */
type EstadoFavoritos = 'sessao' | 'carregando' | 'falha' | 'vazia' | 'pronto';

const ACENTOS = /\p{Diacritic}/gu;
const ORDEM_ALFABETICA = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

const ERRO_REMOVER = 'Não foi possível remover o favorito agora. Tente novamente em instantes.';

function normalizar(valor: string): string {
  return valor.normalize('NFD').replace(ACENTOS, '').toLowerCase().trim();
}

/**
 * A aba "Serviços favoritos": os SERVIÇOS que o cidadão marcou com a estrela na tela de
 * Serviços, gravados na conta dele.
 *
 * Os favoritos vêm da API só como códigos e números; o card é o serviço como o catálogo público
 * o traz (nome, resumo, categorias, ícone e cor do adm), cruzado por `montarFavoritos`. Filtro,
 * busca e ordenação ficam no cliente.
 */
@Component({
  selector: 'app-servicos-favoritos',
  templateUrl: './servicos-favoritos.component.html',
  styleUrls: ['./servicos-favoritos.component.css'],
  imports: [RouterLink, LucideModule],
  host: {
    '(document:keydown.control.k)': 'focarBusca($event)',
  },
})
export class ServicosFavoritosComponent {
  private readonly favoritosService = inject(FavoritosService);
  private readonly catalogo = inject(CatalogoPublicoService);
  private readonly alerta = inject(AlertaService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly campoBusca = viewChild<ElementRef<HTMLInputElement>>('campoBusca');

  readonly termoBusca = signal('');
  readonly categoriaFiltro = signal('');
  readonly ordenacao = signal<Ordenacao>('acessos');
  readonly modoLista = signal(false);
  readonly dicaVisivel = signal(true);

  /** Os favoritos com a remoção no ar: a estrela fica desabilitada até a resposta. */
  private readonly removendo = signal<ReadonlySet<number>>(new Set());

  /** Todos os favoritos que o catálogo conhece, sem filtro. */
  private readonly todos = computed(() =>
    montarFavoritos(this.favoritosService.favoritos() ?? [], this.catalogo.categorias())
  );

  /**
   * As opções do filtro: as categorias presentes nos favoritos, sem repetição e na ordem
   * editorial do menu. O valor é o código, porque o nome não é único no adm.
   */
  readonly categorias = computed<readonly { id: string; titulo: string }[]>(() => {
    const presentes = new Set(this.todos().flatMap((favorito) => favorito.categorias.map((c) => c.id)));

    return this.catalogo
      .categorias()
      .filter((categoria) => presentes.has(categoria.id))
      .map((categoria) => ({ id: categoria.id, titulo: categoria.titulo }));
  });

  /**
   * A falha dos GRUPOS do catálogo também é falha: sem eles as categorias vêm sem serviço, todo
   * favorito seria descartado, e a aba diria "nenhum favorito" a quem tem favoritos.
   */
  readonly estado = computed<EstadoFavoritos>(() => {
    if (!this.favoritosService.logado()) return 'sessao';

    if (this.favoritosService.falhou() || this.catalogo.falhou() || this.catalogo.falhouServicos()) {
      return 'falha';
    }

    if (this.favoritosService.favoritos() === null || this.catalogo.carregando()) return 'carregando';

    return this.todos().length ? 'pronto' : 'vazia';
  });

  readonly favoritos = computed<readonly ServicoFavorito[]>(() => {
    const termo = normalizar(this.termoBusca());
    const categoria = this.categoriaFiltro();

    const filtrados = this.todos().filter((favorito) => {
      if (categoria && !favorito.categorias.some((item) => item.id === categoria)) return false;
      if (!termo) return true;

      return normalizar(
        `${favorito.titulo} ${favorito.descricao} ${favorito.categorias.map((item) => item.titulo).join(' ')}`
      ).includes(termo);
    });

    return [...filtrados].sort((a, b) =>
      this.ordenacao() === 'nome'
        ? ORDEM_ALFABETICA.compare(a.titulo, b.titulo)
        : b.acessos - a.acessos || ORDEM_ALFABETICA.compare(a.titulo, b.titulo)
    );
  });

  readonly temFiltroAtivo = computed(() => !!this.termoBusca() || !!this.categoriaFiltro());

  constructor() {
    // Cada abertura da aba relê: os contadores mudam a cada página de serviço aberta.
    this.favoritosService.carregar();
  }

  /**
   * A categoria do card: a filtrada, quando o serviço pertence a ela; senão a primeira na ordem
   * editorial — a mesma "categoria principal" de Serviços Digitais.
   */
  categoriaExibida(favorito: ServicoFavorito): CategoriaDoFavorito {
    const filtro = this.categoriaFiltro();

    return (filtro && favorito.categorias.find((item) => item.id === filtro)) || favorito.categorias[0];
  }

  estaRemovendo(favorito: ServicoFavorito): boolean {
    return this.removendo().has(favorito.codigo);
  }

  /** O "Tentar novamente": relê os favoritos e o catálogo, de que o card depende. */
  recarregar(): void {
    this.favoritosService.recarregar();
    this.catalogo.recarregar();
  }

  focarBusca(evento: Event): void {
    evento.preventDefault();
    this.campoBusca()?.nativeElement.focus();
  }

  aoBuscar(evento: Event): void {
    this.termoBusca.set((evento.target as HTMLInputElement).value);
  }

  aoFiltrarCategoria(evento: Event): void {
    this.categoriaFiltro.set((evento.target as HTMLSelectElement).value);
  }

  aoOrdenar(evento: Event): void {
    this.ordenacao.set((evento.target as HTMLSelectElement).value as Ordenacao);
  }

  definirModoLista(lista: boolean): void {
    this.modoLista.set(lista);
  }

  fecharDica(): void {
    this.dicaVisivel.set(false);
  }

  limparBusca(): void {
    this.termoBusca.set('');
    this.campoBusca()?.nativeElement.focus();
  }

  limparFiltros(): void {
    this.termoBusca.set('');
    this.categoriaFiltro.set('');
  }

  /**
   * Desfavoritar tira o serviço da lista, então passa por confirmação. O card só sai depois da
   * resposta — o `404` também tira, porque o favorito já não existia.
   */
  async removerFavorito(favorito: ServicoFavorito): Promise<void> {
    if (this.estaRemovendo(favorito)) return;

    const confirmado = await this.alerta.confirmar({
      titulo: 'Remover dos favoritos?',
      texto: `${favorito.titulo} deixa de aparecer nos seus serviços favoritos.`,
      confirmar: 'Sim, remover',
      cancelar: 'Cancelar',
    });

    if (!confirmado) return;

    this.marcarRemovendo(favorito.codigo, true);

    this.favoritosService
      .desfavoritar(favorito.codigo)
      .pipe(
        finalize(() => this.marcarRemovendo(favorito.codigo, false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: () => void this.alerta.erro({ titulo: 'Favoritos', texto: ERRO_REMOVER }),
      });
  }

  private marcarRemovendo(codigo: number, removendo: boolean): void {
    this.removendo.update((atual) => {
      const proximo = new Set(atual);

      if (removendo) {
        proximo.add(codigo);
      } else {
        proximo.delete(codigo);
      }

      return proximo;
    });
  }
}
